import { ReactElement } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ProductsAnalyticsProvider } from '@client/analytics/context/products/ProductsAnalyticsContext';
import { EVENT_SOURCE } from '@client/analytics/types/types';
import { useBreadcrumbs } from '@client/breadcrumbs/hooks';
import { PRINTERS_CATEGORY_URL } from '@client/common/components/InkFinderLink';
import { RemoteRecordHandler } from '@client/common/components/RemoteRecordHandler/RemoteRecordHandler';
import { ensureArray } from '@client/common/utils/array/array';
import { isServer } from '@client/common/utils/env';
import { META_TAGS } from '@client/common/utils/seo/metaTags';
import { config } from '@client/config';
import { useTranslate } from '@client/i18n/hooks';
import { Skeleton } from '@client/product/components/Skeleton';
import { useProductFullEffect } from '@client/product/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useUrlCreator } from '@client/routes/hooks';
import { getCategoryUrlParams, getCdnImageUrl, getProductFallbackImgUrl } from '@client/routes/utils/url';
import { CrumbType } from '@lib/components/Breadcrumbs';
import { ProductPage } from './ProductPage';

export const ProductPageContainer = (): ReactElement => {
  const t = useTranslate();
  const params = useParams<{ sku: string }>();
  const { pathname } = useLocation();
  const product = useProductFullEffect(params.sku || '');
  const { isLoaded, data: productData } = product;
  const urlCreator = useUrlCreator();
  const isProductDataSpecified = !!productData;
  const shopName = config.shopName;
  const title = productData?.description?.title || '';
  const description = (productData?.description?.bulletPoints || []).join(' - ');
  const imagesArray = ensureArray(productData?.cover?.sizes || []);
  const isImagePresent = imagesArray.length && imagesArray[0].link;
  const cdnImageUrl = isImagePresent && getCdnImageUrl(imagesArray[0].link);
  const imageUrl = cdnImageUrl || getProductFallbackImgUrl();
  const ogUrl = isServer ? `${config.url.root}${pathname}` : window.location.href;
  const getBreadcrumbs = (): CrumbType[] => {
    if (!isLoaded || !productData) {
      return [];
    }

    const breadcrumbs: CrumbType[] = productData.breadcrumbs.map(
      (category) => ({
        title: category.label,
        url: `${urlCreator(ROUTES.category)}${getCategoryUrlParams(
          category.url,
        )}`,
      }),
    );
    breadcrumbs.push({
      title: t('SKU') + ' ' + productData.sku,
    });
    return breadcrumbs;
  };

  useBreadcrumbs(
    getBreadcrumbs(),
  );

  const isInkFinderLinkDisplayed = isLoaded && !!productData?.breadcrumbs
    ?.find(el => el.url === PRINTERS_CATEGORY_URL.de || el.url === PRINTERS_CATEGORY_URL.fr);

  const metaTags = !isLoaded || !isProductDataSpecified
    ? []
    : [
      {
        name: META_TAGS.description,
        content: description,
      },
      {
        name: META_TAGS.ogDescription,
        content: description,
      },
      {
        name: META_TAGS.ogImage,
        content: imageUrl,
      },
      {
        name: META_TAGS.ogTitle,
        content: title,
      },
      {
        name: META_TAGS.ogType,
        content: 'product',
      },
      {
        name: META_TAGS.ogUrl,
        content: ogUrl,
      },
      {
        name: META_TAGS.ogSitename,
        content: shopName,
      },
      {
        name: META_TAGS.title,
        content: title,
      },
      {
        name: META_TAGS.twitterCard,
        content: 'summary_large_image',
      },
      {
        name: META_TAGS.twitterDescription,
        content: description,
      },
      {
        name: META_TAGS.twitterImage,
        content: imageUrl,
      },
      {
        name: META_TAGS.twitterTitle,
        content: title,
      },
    ];

  return (
    <ProductsAnalyticsProvider eventSource={EVENT_SOURCE.pdp}>
      <RemoteRecordHandler
        remoteRecord={product}
        disableSpecificErrorHandler={1}
        customLoaderComponent={<Skeleton />}
      >
        <ProductPage
          isInkFinderLinkDisplayed={isInkFinderLinkDisplayed}
          product={product}
          metaTags={metaTags}
        />
      </RemoteRecordHandler>
    </ProductsAnalyticsProvider>
  );
};
